import React from 'react';

import { Box, Button, Divider, Grid, Paper, Switch, Typography } from '@mui/material';
import { isEqual } from 'es-toolkit/compat';

import { SnackVariant } from '@clearverify/constants';

import { useEmployerUpdateManyMutation } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { SxProp } from 'src/types/mui';

import { PeoPayrollProviderLiveStatusTable } from './components';
import { PeoPayrollProviderLiveStatusValues } from './types';
import { prepareUpdateData } from './utils';

export type PeoPayrollProviderLiveStatusFormProps = {
  peoPayrollProviderId: string;
  initialValues: PeoPayrollProviderLiveStatusValues;
  loading: boolean;
};
export const PeoPayrollProviderLiveStatusForm: React.FC<PeoPayrollProviderLiveStatusFormProps> = ({
  peoPayrollProviderId,
  initialValues,
  loading,
}) => {
  const { spawnNotification } = useNotification();
  const [employersLiveStatus, setEmployersLiveStatus] =
    React.useState<PeoPayrollProviderLiveStatusValues>({ isAllLive: false, employers: [] });

  const touch = React.useMemo(() => {
    return !isEqual(employersLiveStatus, initialValues);
  }, [employersLiveStatus, initialValues]);

  const [updateEmployer, { loading: loadingUpdate }] = useEmployerUpdateManyMutation();

  const onSubmit = React.useCallback(async () => {
    try {
      if (employersLiveStatus.isAllLive !== initialValues.isAllLive) {
        await updateEmployer({
          variables: {
            filter: {
              OR: [
                { peoPayrollProviderId: { equals: peoPayrollProviderId } },
                { peo: { peoPayrollProviderId: { equals: peoPayrollProviderId } } },
              ],
            },
            data: {
              setLiveAt: {
                set: employersLiveStatus.isAllLive ? new Date().toISOString() : null,
              },
            },
          },
        });
        spawnNotification(
          'Selected Employers have been set "Live" successfully',
          SnackVariant.success,
        );
      } else {
        const updateData = prepareUpdateData(employersLiveStatus);
        await Promise.all([
          updateEmployer({
            variables: {
              filter: {
                id: { in: updateData.live },
                setLiveAt: { equals: null },
              },
              data: { setLiveAt: { set: new Date().toISOString() } },
            },
          }),
          updateEmployer({
            variables: {
              filter: {
                id: { in: updateData.notLive },
                setLiveAt: { not: { equals: null } },
              },
              data: { setLiveAt: { set: null } },
            },
          }),
        ]);
        spawnNotification(
          'Selected Employers have been set "Live" successfully',
          SnackVariant.success,
        );
      }
    } catch (error) {
      console.error(error);
      spawnNotification('Something went wrong', SnackVariant.error);
    }
  }, [
    employersLiveStatus,
    initialValues.isAllLive,
    peoPayrollProviderId,
    spawnNotification,
    updateEmployer,
  ]);

  React.useEffect(() => {
    setEmployersLiveStatus(initialValues);
  }, [initialValues]);

  const setAllLiveStatus = React.useCallback(() => {
    setEmployersLiveStatus({
      isAllLive: !employersLiveStatus.isAllLive,
      employers: employersLiveStatus.employers,
    });
  }, [employersLiveStatus.employers, employersLiveStatus.isAllLive]);

  const setEmployerUnderPeoLiveStatus = React.useCallback(
    (peoId: string, employerId: string) => {
      const newEmployers = employersLiveStatus.employers.map(item => {
        if (item.id === peoId) {
          const newItem = item.employers?.map(employer => {
            if (employer.id === employerId) {
              return {
                ...employer,
                isLive: !employer.isLive,
              };
            }
            return employer;
          });

          return {
            ...item,
            isLive: newItem?.every(item => item.isLive) || false,
            employers: newItem,
          };
        }
        return item;
      });

      setEmployersLiveStatus({
        ...employersLiveStatus,
        employers: newEmployers,
      });
    },
    [employersLiveStatus],
  );

  const setEmployerLiveStatus = React.useCallback(
    (employerId: string) => {
      const newEmployers = employersLiveStatus.employers.map(item => {
        if (item.id === employerId) {
          return {
            ...item,
            isLive: !item.isLive,
          };
        }
        return item;
      });

      setEmployersLiveStatus({
        ...employersLiveStatus,
        employers: newEmployers,
      });
    },
    [employersLiveStatus],
  );

  const setPeoLiveStatus = React.useCallback(
    (peoId: string) => {
      const newEmployers = employersLiveStatus.employers.map(item => {
        if (item.id === peoId) {
          const newItem = item.employers?.map(employer => {
            return {
              ...employer,
              isLive: !item.isLive,
            };
          });

          return {
            ...item,
            isLive: !item.isLive,
            employers: newItem,
          };
        }
        return item;
      });

      setEmployersLiveStatus({
        ...employersLiveStatus,
        employers: newEmployers,
      });
    },
    [employersLiveStatus],
  );

  return (
    <React.Fragment>
      <Paper variant="outlined" sx={paperStyles} elevation={2}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={checkboxSettingsStyles}>
              <Box>
                <Typography variant="subtitle1">
                  All Employers under PEO Payroll Provider
                </Typography>

                <Typography variant="body1" color="text.secondary" mt={1}>
                  Activate this toggle if all Employers under this PEO PP are ready for
                  verifications
                </Typography>
              </Box>

              <Box paddingRight={2}>
                <Switch
                  checked={employersLiveStatus.isAllLive}
                  onChange={() => setAllLiveStatus()}
                  disabled={loading || !employersLiveStatus.employers.length}
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={12}>
            <Divider>
              <Typography variant="subtitle1">OR</Typography>
            </Divider>
          </Grid>

          <Grid size={12}>
            <Box mb={3}>
              <Typography variant="subtitle1">Each PEO or Employer separately</Typography>

              <Typography variant="body1" color="text.secondary" mt={1}>
                If you want to activate all Employers under PEO – activate the toggle near PEO
                company name.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                If you want to activate separate Employer – activate the toggle near the Employer
                company name.
              </Typography>
            </Box>
            {!!employersLiveStatus.employers.length && (
              <Box>
                <PeoPayrollProviderLiveStatusTable
                  values={employersLiveStatus.employers}
                  setEmployerLiveStatus={setEmployerLiveStatus}
                  setPeoLiveStatus={setPeoLiveStatus}
                  setEmployerUnderPeoLiveStatus={setEmployerUnderPeoLiveStatus}
                  loading={loading}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Box display="flex" gap={3} justifyContent="center">
        <Button
          variant="outlined"
          loading={loading || loadingUpdate}
          disabled={loading || loadingUpdate || !touch}
          onClick={() => setEmployersLiveStatus(initialValues)}
          color="error"
        >
          Reset
        </Button>
        <Button
          variant="contained"
          loading={loading || loadingUpdate}
          disabled={loading || loadingUpdate || !touch}
          onClick={() => onSubmit()}
        >
          Save Changes
        </Button>
      </Box>
    </React.Fragment>
  );
};

const checkboxSettingsStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const paperStyles: SxProp = {
  padding: '16px',
  marginBottom: '16px',
};
